import { useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { displayToastSuccess } from '@/helpers/display-toast';
import { usePermissions } from '@/modules/common';
import { FeatureMode } from '@/modules/common/components/templates/feature-details/types/types';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { useFeatureTabNavigation } from '@/modules/common/hooks/use-feature-tab-navigation';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { MyOpsiumUserProfile } from '@/modules/common/types/opsium-user-profile';
import { PatchEntityResult } from '@/modules/common/types/patch-entity-result';
import {
  invalidateTeamMemberQueries,
  invalidateTeamMemberRelatedQueries,
  invalidateTeamMemberWithRelatedQueries,
  invalidateUserProfileQueries,
} from '@/modules/common/utils/query-invalidation';
import {
  createTeamMember,
  deleteTeamMemberAvatar,
  getTeamMemberDetails,
  updateTeamMemberBilling,
  updateTeamMemberCapacity,
  updateTeamMemberCustomAttributes,
  updateTeamMemberInfo,
  updateTeamMemberIntegrations,
  uploadTeamMemberAvatar,
} from '@/modules/team/api/team.api';
import { TeamMember, TeamMemberFormData, TeamMemberTab } from '@/modules/team/types';

interface TeamMemberSaveParams {
  data: TeamMemberFormData | (Partial<TeamMemberFormData> & { id: string });
  successCallback?: (result: PatchEntityResult) => void;
  errorCallback?: (error: unknown) => void;
}

interface AvatarUploadParams {
  userId: number;
  file: File;
}

/**
 * Manages team member details state and operations for both create and edit modes.
 * Handles team member CRUD operations, tab navigation, and file upload functionality.
 *
 * Features:
 * - Team member creation and update with optimistic UI updates
 * - Tab-based navigation for multi-step team member forms
 * - Avatar upload with cache invalidation
 * - Mode management (Create/Edit/Read) based on user permissions
 * - Integrated error handling with user-friendly toast notifications
 */
export function useTeamMemberDetails() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['authenticated.team']);
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const { hasPermission } = usePermissions();
  const isEditMemberEnabled = hasPermission(OpsiumPolicy.TeamMembersEdit);

  const defaultMode =
    memberId === 'new'
      ? FeatureMode.Create
      : isEditMemberEnabled
        ? FeatureMode.Edit
        : FeatureMode.Read;

  const [mode, setMode] = useState<FeatureMode>(defaultMode);
  const [onSaveSuccess, setOnSaveSuccess] = useState<((result: PatchEntityResult) => void) | null>(
    null
  );

  // Update mode when permissions load or change (handles page refresh scenario)
  useEffect(() => {
    // Only update if we're not in create mode
    if (memberId !== 'new') {
      const newMode = isEditMemberEnabled ? FeatureMode.Edit : FeatureMode.Read;
      setMode(newMode);
    }
  }, [memberId, isEditMemberEnabled]);

  // Use hybrid tab navigation hook
  const { currentTab, setCurrentTab, navigateToTab } = useFeatureTabNavigation({
    mode,
    defaultTab: TeamMemberTab.Info,
    validTabs: Object.values(TeamMemberTab),
    entityId: memberId,
  });

  const {
    data: teamMemberData,
    isLoading: isLoadingTeamMember,
    error: teamMemberError,
  } = useQuery({
    queryKey: ['team-member', memberId],
    queryFn: () => getTeamMemberDetails(memberId!),
    enabled: Boolean(memberId && memberId !== 'new'),
  });

  /**
   * Parses a date string in YYYY-MM-DD format to a Date object at UTC midnight
   */
  const parseDateString = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
  };

  /**
   * Converts effectiveStartDate to Date object, handling string format and null values
   * Falls back to current date if not provided
   */
  const parseEffectiveStartDate = (effectiveStartDate: Date | string | null): Date => {
    if (!effectiveStartDate) return new Date();
    return typeof effectiveStartDate === 'string'
      ? parseDateString(effectiveStartDate)
      : effectiveStartDate;
  };

  /**
   * Converts effectiveEndDate to Date object, handling string format, null, and undefined values
   * Returns undefined for both null (infinite/ongoing) and undefined (not provided)
   */
  const parseEffectiveEndDate = (
    effectiveEndDate: Date | string | null | undefined
  ): Date | undefined => {
    if (effectiveEndDate === undefined || effectiveEndDate === null) return undefined;
    return typeof effectiveEndDate === 'string'
      ? parseDateString(effectiveEndDate)
      : effectiveEndDate;
  };

  const createTeamMemberMutation = useMutation({
    mutationFn: async (data: TeamMemberFormData) => {
      // Step 1: Create the team member
      const memberId = await createTeamMember(data);

      // Step 2: Upload avatar if provided
      if (data.avatarLink instanceof File) {
        await uploadTeamMemberAvatar({ userId: memberId, file: data.avatarLink });
      }

      return memberId;
    },
    onSuccess: async () => {
      // Use shared invalidation utility for consistency
      await invalidateTeamMemberRelatedQueries(queryClient);

      displayToastSuccess(t('authenticated.team:toasts.teamMemberCreatedSuccess'));

      // Call success callback before navigation (allows navigation guard to be bypassed)
      if (onSaveSuccess) {
        onSaveSuccess({ patchedArchivedRanges: [] });
        setOnSaveSuccess(null);
      }

      navigate(`/team`, { replace: true });
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.team:toasts.createTeamMemberError'
      );
    },
  });

  /**
   * Creates a reusable mutation hook for updating team member sections
   */
  const useCreateUpdateMutation = (
    updateFn: (
      memberId: number,
      data: Partial<TeamMemberFormData>,
      effectiveStartDate: Date,
      effectiveEndDate?: Date
    ) => Promise<PatchEntityResult>
  ) => {
    return useMutation({
      mutationFn: async (
        data: Partial<TeamMemberFormData> & {
          id: string;
          effectiveStartDate: string | null;
          effectiveEndDate: string | null;
        }
      ) => {
        const { id, effectiveStartDate, effectiveEndDate, ...updateData } = data;
        const numericId = parseInt(id, 10);

        const startDate = parseEffectiveStartDate(effectiveStartDate);
        const endDate = parseEffectiveEndDate(effectiveEndDate);

        const updateResult = await updateFn(numericId, updateData, startDate, endDate);
        const updatedTeamMember = await getTeamMemberDetails(id);
        // Update specific team member cache
        queryClient.setQueryData(['team-member', updatedTeamMember.id], updatedTeamMember);

        // Invalidate all related queries including this specific member
        await invalidateTeamMemberWithRelatedQueries(queryClient, updatedTeamMember.id);

        // Invalidate team member changes (specific to this member)
        await queryClient.invalidateQueries({
          queryKey: ['team-member-changes', updatedTeamMember.id],
        });

        // If the edited member is the current user, invalidate the user profile cache too
        const currentUser = queryClient.getQueryData<MyOpsiumUserProfile>(['user-profile']);
        if (currentUser && String(currentUser.id) === updatedTeamMember.id) {
          await invalidateUserProfileQueries(queryClient);
        }

        return updateResult;
      },
      onSuccess: updateResult => {
        displayToastSuccess(t('authenticated.team:toasts.teamMemberUpdatedSuccess'));

        if (onSaveSuccess) {
          onSaveSuccess(updateResult);
          setOnSaveSuccess(null);
        }
      },
      onError: error => {
        displayToastErrorFromApiWithFallback(
          error,
          'authenticated.team:toasts.updateTeamMemberError'
        );
      },
    });
  };

  const updateTeamMemberInfoMutation = useCreateUpdateMutation(updateTeamMemberInfo);
  const updateTeamMemberBillingMutation = useCreateUpdateMutation(updateTeamMemberBilling);
  const updateTeamMemberCapacityMutation = useCreateUpdateMutation(updateTeamMemberCapacity);

  const updateCustomAttributesMutation = useMutation({
    mutationFn: async (data: { customAttributesValues: any[] }) => {
      if (!memberId) return;
      await updateTeamMemberCustomAttributes(parseInt(memberId, 10), data.customAttributesValues);
      const updatedTeamMember = await getTeamMemberDetails(memberId);
      queryClient.setQueryData(['team-member', updatedTeamMember.id], updatedTeamMember);
      await invalidateTeamMemberWithRelatedQueries(queryClient, updatedTeamMember.id);
    },
    onSuccess: () => {
      displayToastSuccess(t('authenticated.team:toasts.teamMemberUpdatedSuccess'));
      if (onSaveSuccess) {
        onSaveSuccess({} as PatchEntityResult);
        setOnSaveSuccess(null);
      }
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.team:toasts.updateTeamMemberError'
      );
    },
  });

  const updateTeamMemberIntegrationsMutation = useMutation({
    mutationFn: async (
      data: Partial<TeamMemberFormData> & { id: string }
    ): Promise<PatchEntityResult> => {
      const { id, integrations } = data;

      let updatedTeamMember: TeamMember;
      if (integrations === undefined) {
        updatedTeamMember = await getTeamMemberDetails(id);
      } else {
        await updateTeamMemberIntegrations(id, integrations);
        updatedTeamMember = await getTeamMemberDetails(id);
      }

      // Update specific team member cache
      queryClient.setQueryData(['team-member', updatedTeamMember.id], updatedTeamMember);

      // Invalidate all related queries including this specific member
      await invalidateTeamMemberWithRelatedQueries(queryClient, updatedTeamMember.id);

      // Invalidate team member changes (specific to this member)
      await queryClient.invalidateQueries({
        queryKey: ['team-member-changes', updatedTeamMember.id],
      });

      // Invalidate available integrations to refetch and update the form state
      await queryClient.invalidateQueries({
        queryKey: ['available-integrations', mode, updatedTeamMember.id],
      });

      return {
        patchedArchivedRanges: [],
      };
    },
    onSuccess: async (patchMemberResult: PatchEntityResult) => {
      displayToastSuccess(t('authenticated.team:toasts.teamMemberUpdatedSuccess'));

      if (onSaveSuccess) {
        onSaveSuccess(patchMemberResult);
        setOnSaveSuccess(null);
      }
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.team:toasts.updateTeamMemberError'
      );
    },
  });

  const uploadAvatarMutation = useMutation({
    mutationFn: ({ userId, file }: AvatarUploadParams) => uploadTeamMemberAvatar({ userId, file }),
    onSuccess: async () => {
      await invalidateTeamMemberQueries(queryClient);
      if (memberId && memberId !== 'new') {
        queryClient.invalidateQueries({ queryKey: ['team-member', memberId] });
      }
      // If the edited member is the current user, invalidate the user profile cache too
      const currentUser = queryClient.getQueryData<MyOpsiumUserProfile>(['user-profile']);
      if (currentUser && String(currentUser.id) === memberId) {
        await invalidateUserProfileQueries(queryClient);
      }
      displayToastSuccess(t('authenticated.team:toasts.avatarUploadedSuccess'));
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'authenticated.team:toasts.avatarUploadError');
    },
  });

  const deleteAvatarMutation = useMutation({
    mutationFn: (userId: number) => deleteTeamMemberAvatar(userId),
    onSuccess: async () => {
      await invalidateTeamMemberQueries(queryClient);
      if (memberId && memberId !== 'new') {
        queryClient.invalidateQueries({ queryKey: ['team-member', memberId] });
      }
      // If the edited member is the current user, invalidate the user profile cache too
      const currentUser = queryClient.getQueryData<MyOpsiumUserProfile>(['user-profile']);
      if (currentUser && String(currentUser.id) === memberId) {
        await invalidateUserProfileQueries(queryClient);
      }
      displayToastSuccess(t('authenticated.team:toasts.avatarDeletedSuccess'));
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'authenticated.team:toasts.avatarDeleteError');
    },
  });

  const handleSaveTeamMember = ({ data, successCallback, errorCallback }: TeamMemberSaveParams) => {
    // Store the callback function in state (wrap in arrow function to prevent React from executing it)
    setOnSaveSuccess(() => (result: PatchEntityResult) => {
      if (successCallback) {
        successCallback(result);
      }
    });

    const mutateOptions = errorCallback ? { onError: errorCallback } : undefined;

    if (mode === FeatureMode.Create) {
      createTeamMemberMutation.mutate(data as TeamMemberFormData, mutateOptions);
    } else {
      // Use tab-specific endpoint when on specific tabs
      const updateData = {
        ...data,
        id: memberId!,
      } as Partial<TeamMemberFormData> & {
        id: string;
        effectiveStartDate: string | null;
        effectiveEndDate: string | null;
      };

      if (currentTab === TeamMemberTab.Integrations) {
        updateTeamMemberIntegrationsMutation.mutate(updateData, mutateOptions);
      } else if (currentTab === TeamMemberTab.Billing) {
        updateTeamMemberBillingMutation.mutate(updateData, mutateOptions);
      } else if (currentTab === TeamMemberTab.Capacity) {
        updateTeamMemberCapacityMutation.mutate(updateData, mutateOptions);
      } else {
        // Default to info tab update
        updateTeamMemberInfoMutation.mutate(updateData, mutateOptions);
      }
    }
  };

  const handleTabChange = (
    tab: TeamMemberTab | string,
    additionalParams?: Record<string, string>
  ) => {
    const targetTab = typeof tab === 'string' ? (tab as TeamMemberTab) : tab;

    if (additionalParams) {
      // Use navigateToTab when additional params are provided
      navigateToTab(targetTab, additionalParams);
    } else {
      // Simple tab change without additional params
      setCurrentTab(targetTab);
    }
  };

  const handleAvatarUpload = (file: File) => {
    if (memberId && memberId !== 'new') {
      uploadAvatarMutation.mutate({ userId: parseInt(memberId, 10), file });
    }
  };

  const handleAvatarRemove = () => {
    if (memberId && memberId !== 'new') {
      deleteAvatarMutation.mutate(parseInt(memberId, 10));
    }
  };

  const isLoading =
    createTeamMemberMutation.isPending ||
    updateTeamMemberInfoMutation.isPending ||
    updateTeamMemberBillingMutation.isPending ||
    updateTeamMemberCapacityMutation.isPending ||
    updateTeamMemberIntegrationsMutation.isPending;

  return {
    // Data
    teamMemberData,
    mode,
    currentTab,

    // Loading states
    isLoading,
    isLoadingTeamMember,
    isUploadingAvatar: uploadAvatarMutation.isPending || deleteAvatarMutation.isPending,

    // Error states - only return team member loading errors, not form submission errors
    error: teamMemberError,

    // Actions
    handleSaveTeamMember,
    handleTabChange,
    handleAvatarUpload,
    handleAvatarRemove,
    navigateToTab,
  };
}
